import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Grid,
    Pagination,
    Stack,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    fetchUsers,
    deleteUser,
    toggleUserStatus,
    setFilters,
    clearFilters
} from '../../store/slices/userSlice';
import { User } from '../../services/userApi';
import { useTheme } from '../../contexts/ThemeContext';
import UserForm from './UserForm';
import UserView from './UserView';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

const Users: React.FC = () => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isExtraSmall = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const dispatch = useAppDispatch();
    const { toast, showSuccess, showError, hideToast } = useToast();

    // Debug mobile detection
    console.log('Mobile detection:', { isMobile, isSmallMobile });

    const { users, pagination, filters, loading, error } = useAppSelector((state) => state.users);
    const { user: currentUser } = useAppSelector((state) => state.auth);

    // Local state
    const [searchQuery, setSearchQuery] = useState(filters.query);
    const [selectedType, setSelectedType] = useState(filters.type);
    const [selectedStatus, setSelectedStatus] = useState(filters.status);
    const [currentPage, setCurrentPage] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const loadUsers = React.useCallback(() => {
        const params = {
            page: currentPage,
            per_page: 20,
            ...filters
        };
        dispatch(fetchUsers(params));
    }, [currentPage, filters, dispatch]);

    // Load users on component mount
    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Handle search
    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setCurrentPage(1);
    };

    // Handle filter changes
    const handleTypeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSelectedType(value);
        dispatch(setFilters({ type: value }));
        setCurrentPage(1);
    };

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSelectedStatus(value);
        dispatch(setFilters({ status: value }));
        setCurrentPage(1);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedType('');
        setSelectedStatus('');
        dispatch(clearFilters());
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Handle user actions
    const handleViewUser = (user: User) => {
        setSelectedUser(user);
        setOpenView(true);
    };

    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setOpenForm(true);
    };

    const handleDeleteUser = (user: User) => {
        setUserToDelete(user);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (userToDelete) {
            try {
                await dispatch(deleteUser(userToDelete.id)).unwrap();
                showSuccess(`User "${userToDelete.name}" has been deleted successfully`);
                loadUsers();
            } catch (err: any) {
                showError(err?.message || 'Failed to delete user');
            } finally {
                setDeleteDialog(false);
                setUserToDelete(null);
            }
        }
    };

    const handleToggleStatus = async (user: User) => {
        try {
            const result = await dispatch(toggleUserStatus(user.id)).unwrap();
            const newStatus = result.status === 1 ? 'activated' : 'deactivated';
            showSuccess(`User "${user.name}" has been ${newStatus} successfully`);
            loadUsers();
        } catch (err: any) {
            showError(err?.message || 'Failed to update user status');
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get status chip
    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                icon={<ActiveIcon />}
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                icon={<InactiveIcon />}
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <Box sx={{ p: isExtraSmall ? 0.5 : isMobile ? 1 : 3 }}>
            {/* Header */}
            <Box sx={{
                mb: 3,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 2 : 0
            }}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        textAlign: isMobile ? 'center' : 'left'
                    }}
                >
                    User Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedUser(null);
                        setOpenForm(true);
                    }}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        }
                    }}
                >
                    {isMobile ? 'Add User' : 'Add New User'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: theme.palette.background.paper }}>
                <CardContent sx={{ p: isExtraSmall ? 1 : isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search by name"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={selectedType}
                                    onChange={handleTypeChange}
                                    label="Role"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Roles</MenuItem>
                                    <MenuItem value="ADMIN">Admin</MenuItem>
                                    <MenuItem value="MANAGER">Manager</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="Status"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="1">Active</MenuItem>
                                    <MenuItem value="0">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                fullWidth
                                sx={{ borderRadius: 3 }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card sx={{ bgcolor: theme.palette.background.paper, display: isMobile ? 'none' : 'block' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ position: 'relative', minHeight: 400 }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}

                        <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Login ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" color="textSecondary">
                                                    No users found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        users.map((user) => (
                                            <TableRow key={user.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {user.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{user.loginID}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.type}
                                                        color={user.type === 'ADMIN' ? 'primary' : 'secondary'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {user.profile?.phone || '-'}
                                                    {user.profile?.phone2 && (
                                                        <Typography variant="caption" display="block" color="textSecondary">
                                                            {user.profile.phone2}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {user.profile?.city?.name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(user.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusChip(user.status)}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewUser(user)}
                                                                sx={{ color: theme.palette.primary.main }}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit User">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditUser(user)}
                                                                sx={{ color: theme.palette.secondary.main }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={user.status ? 'Deactivate' : 'Activate'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleStatus(user)}
                                                                sx={{
                                                                    color: user.status
                                                                        ? theme.palette.error.main
                                                                        : theme.palette.success.main
                                                                }}
                                                            >
                                                                {user.status ? <InactiveIcon /> : <ActiveIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        {currentUser?.id !== user.id && (
                                                            <Tooltip title="Delete User">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteUser(user)}
                                                                    sx={{ color: theme.palette.error.main }}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Pagination */}
                    {pagination && pagination.last_page > 1 && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={pagination.last_page}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton={!isMobile}
                                showLastButton={!isMobile}
                                size={isMobile ? "small" : "medium"}
                                siblingCount={isMobile ? 0 : 1}
                            />
                        </Box>
                    )}

                    {/* Results Summary */}
                    {pagination && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {pagination.from} to {pagination.to} of {pagination.total} users
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Mobile Card Layout */}
            {isMobile && (
                <Card sx={{ bgcolor: theme.palette.background.paper, mt: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ position: 'relative', minHeight: 200 }}>
                            {loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 1,
                                        borderRadius: 3
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            {users.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No users found
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={isMobile ? 1 : 2}>
                                    {users.map((user) => (
                                        <Card
                                            key={user.id}
                                            sx={{
                                                bgcolor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 3
                                            }}
                                        >
                                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1 : 2 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 0.5 : 1 }}>
                                                            {user.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: isMobile ? 0.25 : 0.5 }}>
                                                            {user.loginID}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: isMobile ? 0.5 : 1 }}>
                                                            {user.email}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={user.type}
                                                                color={user.type === 'ADMIN' ? 'primary' : 'secondary'}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            {getStatusChip(user.status)}
                                                        </Box>
                                                    </Box>
                                                    <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
                                                        <IconButton
                                                            size={isMobile ? "small" : "small"}
                                                            onClick={() => handleViewUser(user)}
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditUser(user)}
                                                            sx={{
                                                                color: theme.palette.secondary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleStatus(user)}
                                                            sx={{
                                                                color: user.status
                                                                    ? theme.palette.error.main
                                                                    : theme.palette.success.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            {user.status ? <InactiveIcon /> : <ActiveIcon />}
                                                        </IconButton>
                                                        {currentUser?.id !== user.id && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteUser(user)}
                                                                sx={{
                                                                    color: theme.palette.error.main,
                                                                    bgcolor: theme.palette.action.hover,
                                                                    '&:hover': { bgcolor: theme.palette.action.selected }
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </Box>
                                                <Divider sx={{ my: isMobile ? 1 : 2 }} />
                                                <Grid container spacing={isMobile ? 1 : 2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Phone
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {user.profile?.phone || '-'}
                                                        </Typography>
                                                        {user.profile?.phone2 && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {user.profile.phone2}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            City
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {user.profile?.city?.name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Created
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(user.created_at)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Mobile Pagination */}
            {isMobile && pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton={false}
                        showLastButton={false}
                        size="small"
                        siblingCount={0}
                    />
                </Box>
            )}

            {/* Mobile Results Summary */}
            {isMobile && pagination && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} users
                    </Typography>
                </Box>
            )}

            {/* User Form Dialog */}
            <UserForm
                open={openForm}
                user={selectedUser}
                onClose={() => {
                    setOpenForm(false);
                    setSelectedUser(null);
                }}
                onSuccess={(isEdit: boolean, userName: string) => {
                    setOpenForm(false);
                    setSelectedUser(null);
                    loadUsers();
                    if (isEdit) {
                        showSuccess(`User "${userName}" has been updated successfully`);
                    } else {
                        showSuccess(`User "${userName}" has been created successfully`);
                    }
                }}
                onError={(message: string) => {
                    showError(message);
                }}
            />

            {/* User View Dialog */}
            <UserView
                open={openView}
                user={selectedUser}
                onClose={() => {
                    setOpenView(false);
                    setSelectedUser(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete User"
                message={`Are you sure you want to delete "${userToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setUserToDelete(null);
                }}
            />

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default Users; 